import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { AssetService } from '../../services/asset.service';
import { SpaceCardComponent } from '../space-card/space-card.component';
import { SiteFooterComponent } from '../site-footer/site-footer.component';
import { Asset, SPACE_CATEGORY_LABELS, SpaceCategory, getSpaceCategories } from '../../models/asset.model';
import { minHourlyRate, formatEuro } from '../../models/asset.model';

@Component({
  selector: 'app-owner-public',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule, SpaceCardComponent, SiteFooterComponent],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './owner-public.component.html',
})
export class OwnerPublicComponent implements OnInit {
  private route        = inject(ActivatedRoute);
  private router       = inject(Router);
  private assetService = inject(AssetService);
  private firestore    = inject(Firestore);

  readonly ownerSlug    = signal('');
  readonly ownerUid     = signal('');
  readonly spaces       = signal<Asset[]>([]);
  readonly loading      = signal(true);
  readonly notFound     = signal(false);

  /** Owner display name derived from the first space (denormalized). */
  readonly ownerName = computed(() => {
    const s = this.spaces();
    return s.length > 0 ? (s[0].ownerDisplayName?.trim() || 'Propietario') : 'Propietario';
  });

  /** Owner initials for the avatar. */
  readonly ownerInitials = computed(() => {
    const name = this.ownerName();
    return name
      .split(' ')
      .slice(0, 2)
      .map(w => w[0]?.toUpperCase() ?? '')
      .join('');
  });

  /** Category breakdown for the "tags" badge row. */
  readonly categorySummary = computed(() => {
    const counts = new Map<string, number>();
    for (const s of this.spaces()) {
      for (const cat of getSpaceCategories(s)) {
        counts.set(cat, (counts.get(cat) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries()).map(([cat, count]) => ({
      cat,
      label: SPACE_CATEGORY_LABELS[cat as SpaceCategory] ?? cat,
      count,
    }));
  });

  readonly formatEuro = formatEuro;
  readonly minHourlyRate = minHourlyRate;

  ngOnInit(): void {
    const slug = this.route.snapshot.paramMap.get('slug') ?? '';
    if (!slug) { void this.router.navigate(['/404']); return; }
    this.ownerSlug.set(slug);
    void this.loadOwnerProfile(slug);
  }

  /**
   * Resolves the slug to a uid via `/owner_slugs/{slug}` (PII-free public
   * lookup) and then loads the owner's active spaces. Slug not found →
   * render the 404 state; owner has zero active spaces → same treatment.
   */
  private async loadOwnerProfile(slug: string): Promise<void> {
    try {
      const slugSnap = await getDoc(doc(this.firestore, 'owner_slugs', slug));
      if (!slugSnap.exists()) {
        this.notFound.set(true);
        return;
      }
      const uid = (slugSnap.data() as { uid?: string }).uid ?? '';
      if (!uid) {
        this.notFound.set(true);
        return;
      }
      this.ownerUid.set(uid);

      const spaces = await this.assetService.getActiveSpacesByOwner(uid);
      if (spaces.length === 0) {
        this.notFound.set(true);
      } else {
        this.spaces.set(spaces);
      }
    } catch {
      this.notFound.set(true);
    } finally {
      this.loading.set(false);
    }
  }
}
